import { useTranslation } from 'react-i18next';
import { SideDrawer, Skeleton, SubPartnerAccount, withStyles } from 'vopay';
import { BodyText } from 'UI';
import { Permissions } from 'shared/permissions';
import usePartnerPermissionSections from '@/components/Partners/PartnerManagement/usePartnerPermissionSections';
import styles from './PartnerPermissionsDrawer.module.scss';

const cn = withStyles(styles);

const SKELETON_ROW_COUNT = 6;

type PartnerPermissionsDrawerProps = {
  partner: SubPartnerAccount;
  isOpen: boolean;
  onClose: () => void;
};

/**
 * The Partner Permissions drawer (spec 5.7), opened by the list drawer's View Permissions: the partner
 * account's own payment-rail toggles, read-only — the defaults its accounts inherit when created. Laid
 * out as the sub-account list's permissions drawer (shared `SubAccountList/Permissions`), over the same
 * tree as the partner page's Permissions tab. Platform capabilities are the Partner Entitlement tab (5.6.3).
 */
export default function PartnerPermissionsDrawer({ partner, isOpen, onClose }: PartnerPermissionsDrawerProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Partners.PartnerManagement.PartnerPermissionsDrawer' });
  const { sections, isLoading, message } = usePartnerPermissionSections(partner.PartnerAccountID, isOpen);

  return (
    // Read-only: there is no Save, so onSave never runs.
    <Permissions
      sections={sections}
      currency={partner.Currency}
      readOnly
      formID="partnerPermissionsForm"
      onSave={() => undefined}
    >
      <SideDrawer
        isOpen={isOpen}
        onClose={onClose}
        headerClassName={cn('whiteBackground')}
        title={
          <div className={cn('header')}>
            <span>{t('title', { defaultValue: 'Partner Permissions' })}</span>

            <BodyText weight="md" className={cn('partnerName')}>
              {partner.Name}
            </BodyText>
          </div>
        }
        headerFullWidthContent={
          <div className={cn('searchWrapper')}>
            <Permissions.Search />
          </div>
        }
      >
        <BodyText className={cn('note')}>
          {t('note', {
            defaultValue: "These are the defaults the partner account's accounts inherit when they are created.",
          })}
        </BodyText>

        {isLoading && (
          <div className={cn('skeletons')} data-testid="partnerPermissionsSkeleton">
            {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
              <Skeleton key={index} height="56px" />
            ))}
          </div>
        )}

        {!isLoading && (message ? <BodyText className={cn('note')}>{message}</BodyText> : <Permissions.Sections />)}
      </SideDrawer>
    </Permissions>
  );
}
